import { request } from "@/api/client";
import type { ArtistSubmission, OnboardingStatus, OrganizationSubmission } from "@/types/onboarding";

export type AccountOnboardingPayload = {
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  organization_name?: string;
  organization_address?: {
    line1: string;
    line2?: string;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  phone_number?: string;
  user_type?: "artist" | "manager" | "both";
  agree_to_terms: boolean;
  tavily_api_key?: string;
  bandsintown_app_id?: string;
  spotify_client_id?: string;
  spotify_client_secret?: string;
};

export function fetchOnboardingStatus() {
  return request<OnboardingStatus>("/onboarding/status");
}

export function submitAccountOnboarding(payload: AccountOnboardingPayload) {
  return request<OnboardingStatus>("/onboarding/account", {
    method: "POST",
    body: payload,
  });
}

export function submitOrganizationOnboarding(payload: OrganizationSubmission) {
  return request<OnboardingStatus>("/onboarding/organization", {
    method: "POST",
    body: payload,
  });
}

export function submitArtistOnboarding(payload: ArtistSubmission) {
  return request<OnboardingStatus>("/onboarding/artist", {
    method: "POST",
    body: payload,
  });
}

export function linkImapEmail(payload: {
  email_address?: string;
  imap_host?: string;
  imap_port?: number;
  use_ssl?: boolean;
  username?: string;
  password?: string;
  skip?: boolean;
}) {
  return request<{ status: string; message?: string | null; email_address?: string | null }>(
    "/onboarding/email/link_imap",
    {
      method: "POST",
      body: payload,
    },
  );
}

// ---------------------------------------------------------------------------
// MVP activation (V5): goals + comparables. Database-only on the server.
// ---------------------------------------------------------------------------

export const CAREER_GOAL_OPTIONS: { id: string; label: string }[] = [
  { id: "book_more_shows", label: "Book more shows" },
  { id: "build_a_tour", label: "Build a tour" },
  { id: "find_better_venues", label: "Find better venues" },
  { id: "grow_new_markets", label: "Grow into new markets" },
  { id: "understand_business", label: "Understand my music business" },
];

export function submitGoalsOnboarding(goals: string[]) {
  return request<OnboardingStatus>("/onboarding/goals", { method: "POST", body: { goals } });
}

export type ComparableCandidate = {
  kind: "public" | "resolved";
  id: number;
  name: string;
  genre: string | null;
  genres?: string[];
  matched_genres?: string[];
  has_venue_history: boolean;
};

export function fetchComparableCandidates(query?: string, scope: "matching" | "all" = "matching") {
  const params = new URLSearchParams({ scope });
  if (query?.trim()) params.set("q", query.trim());
  return request<{ candidates: ComparableCandidate[]; chosen_genres?: string[]; scope?: "matching" | "all" }>(`/onboarding/comparable-candidates?${params}`);
}

export function submitComparablesOnboarding(payload: {
  public_artist_ids?: number[];
  resolved_artist_ids?: number[];
  skip?: boolean;
}) {
  return request<OnboardingStatus>("/onboarding/comparables", { method: "POST", body: payload });
}
